import { Injectable, InjectionToken } from '@angular/core';
import { LangType, normalizeLanguagePreference } from '../types/language';

/**
 * Storage for the user's explicitly selected language preference.
 * Only written when the user chooses a language in the UI (e.g. header dropdown).
 * Used on init to distinguish "user has chosen before" vs "use system language (browser/org-unit/default)".
 */
export interface LanguagePreferenceStorage {
  getUserPreferredLanguage(): LangType | null;
  setUserPreferredLanguage(lang: LangType): void;
}

export const USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE =
  new InjectionToken<LanguagePreferenceStorage>(
    'USER_SELECTED_LANGUAGE_PREFERENCE_STORAGE',
  );

const STORAGE_KEY = 'wilson.language.userPreference';

/**
 * Persists user language preference in localStorage.
 * Use in the portal (and other web apps) so preference survives reloads.
 */
@Injectable()
export class LocalStorageLanguagePreferenceStorage
  implements LanguagePreferenceStorage
{
  getUserPreferredLanguage(): LangType | null {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return normalizeLanguagePreference(raw);
  }

  setUserPreferredLanguage(lang: LangType): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, lang);
  }
}
